import {
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { auditTime, Subject, takeUntil } from 'rxjs';
import {
  ActivityObserverService,
  cookieStorage,
  signalStorage,
} from 'wflow-cdk';

/**
 * The default interval in seconds to audit the activity.
 */
const DEFAULT_INTERVAL = 2;

@Injectable({ providedIn: 'root' })
export class InteractionListenerService implements OnDestroy {
  readonly activityObserverSvc = inject(ActivityObserverService);
  readonly destroyRef = inject(DestroyRef);
  readonly document = inject(DOCUMENT);

  isActivated = false;

  private readonly storage = cookieStorage;
  private readonly storageKey = 'w._last_activity_timestamp_';
  readonly lastActivityTimestamp = signalStorage<number | null>(
    this.storageKey,
    {
      storage: this.storage,
      serializer: (value) => value?.toString() ?? null,
      deserializer: (value) => (value ? parseInt(value) : null),
      initialValue: null,
    },
  );

  private onActivityTimestampChanged = (timestamp: number | null) => void 0;
  /**
   * To register a callback to be called when the activity timestamp changes.
   * @param callback - The callback to call when the activity timestamp changes.
   * @argument timestamp - The timestamp of the activity (in milliseconds).
   */
  readonly registerOnActivityTimestampChangedCallback = (
    callback: (timestamp: number | null) => void,
  ) => {
    if (typeof callback !== 'function') return;
    this.onActivityTimestampChanged = callback;
  };

  private readonly stop$ = new Subject<void>();

  constructor() {
    this.activityObserverSvc.initialize(this.document.body, {
      events: [
        'click',
        'focus',
        'keyup',
        'keypress',
        'mousemove',
        'mouseenter',
        'mouseup',
        'mousedown',
        'mousewheel',
        'scroll',
      ],
      destroyRef: this.destroyRef,
      mergeTabsEvents: true,
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.stop$.complete();
    this.activityObserverSvc.destroy();
  }

  /**
   * Listen to the activity and audit it.
   * @param idleTimeoutSeconds - The idle timeout in seconds.
   */
  listen(idleTimeoutSeconds: number | null) {
    if (this.isActivated) return;
    this.isActivated = true;

    if (idleTimeoutSeconds) {
      this.storage.setKeyTTL(this.storageKey, idleTimeoutSeconds);
    }

    this.activityObserverSvc
      .getActivity()
      .pipe(
        auditTime(DEFAULT_INTERVAL * 1000),
        takeUntil(this.stop$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((activity) => {
        const timestamp = activity.timestamp;
        // Call the callback to notify the activity timestamp changed.
        this.onActivityTimestampChanged(timestamp);

        // If the user is idle, return.
        if (idleTimeoutSeconds && this.isIdle(idleTimeoutSeconds)) return;

        // Update the last activity timestamp when is not idle.
        this.lastActivityTimestamp.set(timestamp);
      });
  }

  /**
   * Stop listening to the activity.
   */
  stop() {
    if (!this.isActivated) return;
    this.isActivated = false;
    this.stop$.next();
    this.lastActivityTimestamp.set(null);
  }

  /**
   * Refresh the activity timestamp to the current time.
   * Used to prevent stale timestamps from triggering immediate idle detection after login.
   */
  refreshActivityTimestamp() {
    this.lastActivityTimestamp.set(Date.now());
  }

  /**
   * Check if the user is idle.
   *
   * @param interval - The interval in seconds to check if the user is idle.
   * @returns True if the user is idle, false otherwise.
   */
  isIdle(interval?: number) {
    const lastActivityTimestamp = this.lastActivityTimestamp();

    if (!interval || !lastActivityTimestamp) return false;
    return Date.now() - lastActivityTimestamp > interval * 1000;
  }
}
